/**
 * Webhook Status Card Component
 *
 * Displays webhook subscription status showing which webhooks are active,
 * any failed deliveries, and provides a re-subscribe action if needed.
 *
 * Features:
 * - Shows list of subscribed webhook topics with status badges
 * - Green badge for active subscriptions
 * - Red badge for failed/missing subscriptions
 * - Displays last webhook received timestamp if available
 * - Shows warning if any subscriptions are missing
 * - Re-subscribe button to fix missing subscriptions
 * - Loading skeleton state
 *
 * @see docs/PRD.md for webhook requirements
 */

import { useState, useCallback } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
  Box,
  Divider,
  Tooltip,
} from '@shopify/polaris';
import type { WebhookStatus, WebhookSubscription, ApiResult } from '../types/api';
import { resubscribeWebhooks } from '../lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface WebhookStatusCardProps {
  /** Webhook status data */
  status: WebhookStatus | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
  /** Callback when status changes (after re-subscribe) */
  onStatusChange?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Human-readable labels for webhook topics
 */
const TOPIC_LABELS: Record<string, string> = {
  'products/create': 'Product Created',
  'products/update': 'Product Updated',
  'products/delete': 'Product Deleted',
  'collections/create': 'Collection Created',
  'collections/update': 'Collection Updated',
  'collections/delete': 'Collection Deleted',
  'pages/create': 'Page Created',
  'pages/update': 'Page Updated',
  'pages/delete': 'Page Deleted',
  'articles/create': 'Article Created',
  'articles/update': 'Article Updated',
  'articles/delete': 'Article Deleted',
  'app/uninstalled': 'App Uninstalled',
  'app_subscriptions/update': 'Subscription Updated',
};

/**
 * Format relative time for last received timestamp
 */
function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return 'Never';

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  // Format as date for older timestamps
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Get human-readable label for a webhook topic
 */
function getTopicLabel(topic: string): string {
  return TOPIC_LABELS[topic] || topic;
}

// =============================================================================
// Skeleton Component
// =============================================================================

/**
 * Loading skeleton for WebhookStatusCard
 */
function WebhookStatusCardSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonDisplayText size="small" />
        <SkeletonBodyText lines={3} />
        <Divider />
        <SkeletonBodyText lines={4} />
      </BlockStack>
    </Card>
  );
}

// =============================================================================
// Subscription Row Component
// =============================================================================

interface SubscriptionRowProps {
  subscription: WebhookSubscription;
}

/**
 * Single row displaying a webhook subscription's status
 */
function SubscriptionRow({ subscription }: SubscriptionRowProps) {
  const { topic, active, lastDeliveryAt, failedDeliveries } = subscription;

  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false}>
      <Box minWidth="0" maxWidth="70%">
        <Text as="span" variant="bodyMd" truncate>
          {getTopicLabel(topic)}
        </Text>
      </Box>
      <InlineStack gap="200" blockAlign="center">
        {lastDeliveryAt && (
          <Tooltip content={`Last received: ${new Date(lastDeliveryAt).toLocaleString()}`}>
            <Text as="span" variant="bodySm" tone="subdued">
              {formatRelativeTime(lastDeliveryAt)}
            </Text>
          </Tooltip>
        )}
        {failedDeliveries > 0 && (
          <Tooltip content={`${failedDeliveries} consecutive failed deliveries`}>
            <Badge tone="warning">{`${failedDeliveries} failed`}</Badge>
          </Tooltip>
        )}
        <Badge tone={active ? 'success' : 'critical'}>
          {active ? 'Active' : 'Missing'}
        </Badge>
      </InlineStack>
    </InlineStack>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Card displaying webhook subscription status with list of topics and actions.
 *
 * @example
 * ```tsx
 * <WebhookStatusCard
 *   status={webhookStatus}
 *   loading={isLoading}
 *   error={errorMessage}
 *   onStatusChange={handleRefresh}
 * />
 * ```
 */
export function WebhookStatusCard({
  status,
  loading = false,
  error = null,
  onStatusChange,
}: WebhookStatusCardProps) {
  const [resubscribing, setResubscribing] = useState(false);
  const [resubscribeError, setResubscribeError] = useState<string | null>(null);
  const [resubscribeSuccess, setResubscribeSuccess] = useState(false);

  // Handle re-subscribe button click
  const handleResubscribe = useCallback(async () => {
    setResubscribing(true);
    setResubscribeError(null);
    setResubscribeSuccess(false);

    try {
      const result: ApiResult<WebhookStatus> = await resubscribeWebhooks();

      if (result.success) {
        setResubscribeSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setResubscribeSuccess(false), 3000);
        // Trigger status refresh
        onStatusChange?.();
      } else {
        setResubscribeError(result.error.error);
      }
    } catch (err) {
      setResubscribeError('Failed to re-subscribe. Please try again.');
    } finally {
      setResubscribing(false);
    }
  }, [onStatusChange]);

  // Loading state
  if (loading) {
    return <WebhookStatusCardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Webhook Status
          </Text>
          <Banner tone="critical">
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  // No data state
  if (!status) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Webhook Status
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            No webhook data available.
          </Text>
        </BlockStack>
      </Card>
    );
  }

  const { subscriptions, allActive, activeCount, totalCount, lastWebhookReceivedAt } = status;
  const missingCount = totalCount - activeCount;

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header with status badge */}
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm" tone="subdued">
            Webhook Status
          </Text>
          <Badge tone={allActive ? 'success' : 'warning'}>
            {`${activeCount} / ${totalCount} Active`}
          </Badge>
        </InlineStack>

        {/* Warning banner if subscriptions are missing */}
        {!allActive && (
          <Banner
            tone="warning"
            action={{
              content: 'Re-subscribe',
              onAction: handleResubscribe,
              loading: resubscribing,
            }}
          >
            {missingCount} webhook subscription{missingCount === 1 ? ' is' : 's are'} missing.
            Click re-subscribe to restore them.
          </Banner>
        )}

        {/* Re-subscribe error */}
        {resubscribeError && (
          <Banner tone="critical" onDismiss={() => setResubscribeError(null)}>
            {resubscribeError}
          </Banner>
        )}

        {/* Re-subscribe success */}
        {resubscribeSuccess && (
          <Banner tone="success" onDismiss={() => setResubscribeSuccess(false)}>
            Webhooks re-subscribed successfully.
          </Banner>
        )}

        {/* Last webhook received timestamp */}
        {lastWebhookReceivedAt && (
          <Text as="p" variant="bodySm" tone="subdued">
            Last webhook received: {formatRelativeTime(lastWebhookReceivedAt)}
          </Text>
        )}

        <Divider />

        {/* Subscription list */}
        <BlockStack gap="300">
          <Text as="h4" variant="headingXs" tone="subdued">
            Subscriptions
          </Text>
          {subscriptions.map((subscription) => (
            <SubscriptionRow
              key={subscription.topic}
              subscription={subscription}
            />
          ))}
        </BlockStack>

        {/* Re-subscribe button when all are active (for manual refresh) */}
        {allActive && (
          <Box paddingBlockStart="200">
            <Button
              variant="plain"
              onClick={handleResubscribe}
              loading={resubscribing}
            >
              Refresh webhook status
            </Button>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}
